import { useLoaderData } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
// import { useTranslation } from 'react-i18next';

// Services
import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';
import { getPage, type PageType } from '@/services/pages/pagesService';

// Components
import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import { PageBlocks } from '@/components/blocks/Blocks';

// Utils
import { getLocale, getSlug } from '@/utils/localeSlug';
import { buildSEOUrl, getAlternateLocalizations } from '@/utils/seo';
import { withTrailingSlashIfInternal } from '@/utils/trailingSlash';

// Styles
import styles from './Page.module.css';

export async function pageLoader({
  request,
  cmsSlug,
}: {
  request: Request;
  /** Slug tel que stocké dans le CMS (peut contenir la locale). Par défaut : déduit de l'URL. */
  cmsSlug?: string;
}) {
  const url = new URL(request.url);

  const locale = getLocale(url);
  const slug = cmsSlug ?? getSlug(url);

  const [header, footer, page ] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
    getPage(slug, locale),
  ]);

  return {
    header,
    footer,
    page,
  };
}

function Page() {
  const baseUrl = import.meta.env.VITE_DOMAIN;
  // API Calls
  const data = useLoaderData() as Awaited<ReturnType<typeof pageLoader>>;

  if (!data) return null;

  const { header, footer, page } = data;

  if (!page) return null;

  return (
    <div>
      <Head htmlAttributes={{ lang: page.locale }}>
        <title>{page.seo.metaTitle}</title>
        <meta name="description" content={page.seo.metaDescription} />
        <link
          rel='canonical'
          href={page.seo.canonicalLink
            ? withTrailingSlashIfInternal(page.seo.canonicalLink, baseUrl)
            : buildSEOUrl(baseUrl, page.locale, page.slug)}
        />
        <link
          rel='alternate' hrefLang={page.locale}
          href={buildSEOUrl(baseUrl, page.locale, page.slug)}
        />
        {
          page.localizations.length > 0 ? (
            getAlternateLocalizations(page.localizations, { requireSlug: true }).map((localization) => (
              <link
                key={localization.documentId}
                rel="alternate"
                hrefLang={localization.locale}
                href={buildSEOUrl(baseUrl, localization.locale, localization.slug)}
              />
            ))
          ) : null
        }
        {
          page.locale === 'fr-FR' ? (
            <link
              rel="alternate"
              hrefLang="x-default"
              href={buildSEOUrl(baseUrl, page.locale, page.slug)}
            />
          ) : (
            (() => {
              const fr = page.localizations.find(loc => loc.locale === 'fr-FR');
              if (fr) {
                return (
                  <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={buildSEOUrl(baseUrl, fr.locale, fr.slug)}
                  />
                );
              }
              return null;
            })()
          )
        }
      </Head>

      <Header
        color={page.headerColor === 'noir' ? 'dark' : 'light'}
        header={header as HeaderType}
        localizations={page.localizations}
      />
      <div className={styles.body}>
        <PageBlocks blocks={(page as PageType).blocks} />
      </div>

      <Footer
        footer={footer as FooterType}
      />
    </div>
  );
}

export default Page;